import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  ShowChart,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Settings,
  Info,
} from '@mui/icons-material';
import { Recommendation, Position } from '../types';
import { PositionService } from '../services/positionService';
import { format } from 'date-fns';

interface ExecutedRecommendationListProps {
  recommendations: Recommendation[];
  onChartOpen: (symbol: string, pivotPrice: number | undefined, currentPrice: number, priceChangePercent: number) => void;
  onInfoOpen: (symbol: string) => void;
  formatCurrency: (value: number) => string;
}

const ExecutedRecommendationList: React.FC<ExecutedRecommendationListProps> = ({
  recommendations,
  onChartOpen,
  onInfoOpen,
  formatCurrency,
}) => {
  const [positionMap, setPositionMap] = useState<Record<string, Position>>({});

  // Look up closed positions for exit recommendations to show gain/loss
  useEffect(() => {
    const exitRecs = recommendations.filter(r => r.type === 'exit' && r.positionId);
    if (exitRecs.length === 0) return;

    const lookupPositions = async () => {
      const map: Record<string, Position> = {};
      for (const rec of exitRecs) {
        if (rec.positionId && !positionMap[rec.positionId]) {
          try {
            const position = await PositionService.getPosition(rec.positionId);
            if (position) map[position.id] = position;
          } catch { /* ignore lookup failures */ }
        }
      }
      if (Object.keys(map).length > 0) {
        setPositionMap(prev => ({ ...prev, ...map }));
      }
    };
    lookupPositions();
  }, [recommendations]); // eslint-disable-line react-hooks/exhaustive-deps

  if (recommendations.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Executed Recommendations
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Recommendations that have been executed will appear here.
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Execute entries, exits, or adjustments to see your trading history.
        </Typography>
      </Paper>
    );
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'entry':
        return <ShoppingCart fontSize="small" />;
      case 'exit':
        return <TrendingUp fontSize="small" />;
      case 'adjustment':
        return <Settings fontSize="small" />;
      default:
        return <CheckCircle fontSize="small" />;
    }
  };

  const getTypeColor = (type: string): 'success' | 'error' | 'info' | 'default' => {
    switch (type) {
      case 'entry':
        return 'success';
      case 'exit':
        return 'error';
      case 'adjustment':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'entry':
        return 'Entry';
      case 'exit':
        return 'Exit';
      case 'adjustment':
        return 'Adjustment';
      default:
        return type;
    }
  };

  const getExitReasonLabel = (reason?: string) => {
    switch (reason) {
      case 'target_hit':
        return 'Target Hit';
      case 'stop_hit':
        return 'Stop Loss';
      case 'time_limit':
        return 'Time Limit';
      case 'stagnant':
        return 'Stagnant';
      case 'better_opportunity':
        return 'Better Opportunity';
      default:
        return reason || 'Manual Exit';
    }
  };

  const getAdjustmentLabel = (adjustmentType?: string) => {
    switch (adjustmentType) {
      case 'raise_stop':
        return 'Raise Stop Loss';
      case 'lower_target':
        return 'Lower Target';
      case 'add_position':
        return 'Add to Position';
      default:
        return adjustmentType || 'Adjustment';
    }
  };

  return (
    <Stack spacing={2}>
      {recommendations.map((rec) => (
        <Card
          key={rec.id}
          elevation={2}
          sx={{
            borderLeft: 4,
            borderLeftColor:
              rec.type === 'entry' ? 'success.main' :
              rec.type === 'exit' ? 'error.main' :
              'info.main',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                {/* Header Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">{rec.symbol}</Typography>
                  <Chip
                    icon={getTypeIcon(rec.type)}
                    label={getTypeLabel(rec.type)}
                    color={getTypeColor(rec.type)}
                    size="small"
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label="Executed"
                    size="small"
                    variant="outlined"
                    color="success"
                  />
                  {rec.type === 'exit' && rec.exitReason && (
                    <Chip
                      label={getExitReasonLabel(rec.exitReason)}
                      size="small"
                      variant="outlined"
                      color={rec.exitReason === 'target_hit' ? 'success' : rec.exitReason === 'stop_hit' ? 'error' : 'default'}
                    />
                  )}
                </Box>

                {/* Execution Date */}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {rec.executedAt ? format(rec.executedAt, 'MMM d, yyyy h:mm a') : format(rec.generatedAt, 'MMM d, yyyy h:mm a')}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {/* Entry Execution Details */}
                {rec.type === 'entry' && (
                  <Box>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        <strong>Entry @ {formatCurrency(rec.recommendedEntryPrice || rec.currentPrice)}</strong>
                        {rec.recommendedQuantity && ` | ${rec.recommendedQuantity} shares`}
                        {rec.recommendedQuantity && rec.recommendedEntryPrice &&
                          ` | Total: ${formatCurrency(rec.recommendedQuantity * rec.recommendedEntryPrice)}`}
                      </Typography>
                      {(rec.recommendedStopLoss || rec.recommendedTarget) && (
                        <Typography variant="body2" color="textSecondary">
                          {rec.recommendedStopLoss && `Stop: ${formatCurrency(rec.recommendedStopLoss)}`}
                          {rec.recommendedStopLoss && rec.recommendedTarget && ' | '}
                          {rec.recommendedTarget && `Target: ${formatCurrency(rec.recommendedTarget)}`}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Exit Execution Details */}
                {rec.type === 'exit' && (() => {
                  const pos = rec.positionId ? positionMap[rec.positionId] : undefined;
                  const qty = rec.recommendedQuantity || pos?.quantity;
                  const entryPrice = pos?.entryPrice;
                  const gainLoss = entryPrice && qty
                    ? (rec.currentPrice - entryPrice) * qty : undefined;
                  const gainLossPercent = entryPrice
                    ? ((rec.currentPrice - entryPrice) / entryPrice) * 100 : undefined;
                  const isPositive = (gainLoss ?? 0) >= 0;

                  return (
                    <Box>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          <strong>Exit @ {formatCurrency(rec.currentPrice)}</strong>
                          {entryPrice && ` (entry @ ${formatCurrency(entryPrice)})`}
                          {qty && ` | ${qty} shares`}
                        </Typography>
                        {gainLoss !== undefined && (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {isPositive ? (
                              <TrendingUp fontSize="small" color="success" />
                            ) : (
                              <TrendingDown fontSize="small" color="error" />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={isPositive ? 'success.main' : 'error.main'}
                            >
                              {isPositive ? '+' : ''}{formatCurrency(gainLoss)}
                              {gainLossPercent !== undefined &&
                                ` (${isPositive ? '+' : ''}${gainLossPercent.toFixed(1)}%)`}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  );
                })()}

                {/* Adjustment Execution Details */}
                {rec.type === 'adjustment' && (
                  <Box>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        <strong>Type:</strong> {getAdjustmentLabel(rec.adjustmentType)}
                      </Typography>
                      {rec.adjustmentValue && (
                        <Typography variant="body2">
                          <strong>New Value:</strong> {formatCurrency(rec.adjustmentValue)}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Reasons (if any) */}
                {rec.reasons && rec.reasons.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      {rec.reasons[0]}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Chart and Info Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => onChartOpen(rec.symbol, undefined, rec.currentPrice, 0)}
                    title="View chart"
                  >
                    <ShowChart />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="default"
                    onClick={() => onInfoOpen(rec.symbol)}
                    title="View stock info"
                  >
                    <Info />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default ExecutedRecommendationList;
